import FilterSection from "@/components/Marketplace/FilterSection";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import InAppBrowserDownloadNotice from "@/components/ui/InAppBrowserDownloadNotice";
import { useFulfillCheckoutSession } from "@/hooks/useFulfillCheckoutSession";
import { downloadAlbum } from "@/lib/utils/album-utils";
import { datadogRum } from "@datadog/browser-rum";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ICellRendererParams, ValueFormatterParams } from "ag-grid-community";
import { format, parseISO } from "date-fns";
import { useCallback, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";

import DownloadIcon from "@/assets/icons/Download.svg?react";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import Table from "@/components/ui/Table";
import { AlbumOrderResponseDto } from "@/lib/lutely-api/gen/dtos";
import { usePurchaseControllerGetAllPurchases } from "@/lib/lutely-api/gen/purchase/purchase";
import { z } from "zod";
import { noindexHead } from "@/lib/seo";

const searchSchema = z.object({
  session_id: z.string().optional(),
});

export const Route = createFileRoute("/account/purchases")({
  component: () => <Purchases />,
  validateSearch: searchSchema,
  head: () => noindexHead("My Purchases | Lutely"),
});

function Purchases() {
  const { data: user } = useLoggedInUser();

  const { data: allPurchases, refetch } = usePurchaseControllerGetAllPurchases({
    query: { enabled: !!user },
  });

  const { session_id } = Route.useSearch();
  const navigate = Route.useNavigate();

  const handleCheckoutSuccess = useCallback(() => {
    navigate({ search: {}, replace: true });
  }, [navigate]);

  const handleCheckoutSettled = useCallback(() => {
    refetch();
  }, [refetch]);

  useFulfillCheckoutSession({
    sessionId: session_id,
    onSuccess: handleCheckoutSuccess,
    onSettled: handleCheckoutSettled,
  });

  // Legacy marketplace licenses (beats, sound kits, songs) stay stored but
  // are no longer listed (LUT-982).
  const purchaseItems = [...(allPurchases?.albumOrders ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <ContainerWrapper>
      <Flex className="pt-[25px]">
        <FilterSection />
        <Flex direction="column" className="pl-0 lg:pl-[50px] flex-1">
          <Text className="font-bold text-[25px]">Purchases</Text>
          <PurchasesTable rowData={purchaseItems} />
        </Flex>
      </Flex>
    </ContainerWrapper>
  );
}

function PurchasesTable({ rowData }: { rowData: AlbumOrderResponseDto[] }) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const isMobile = useMediaQuery("(max-width: 767px)");

  // Refunded albums render "Refunded" in place of the download control, so a
  // list of nothing but those has no Download to tap and the warning would be
  // telling the user to retry an action that is not on screen.
  const hasDownloadableItem = rowData.some((item) => item.refundedAt == null);

  const columns = [
    {
      field: "album",
      headerName: "Item",
      flex: 1,
      minWidth: 200,
      cellStyle: { border: "none" },
      cellRenderer: ({ data }: ICellRendererParams<AlbumOrderResponseDto>) => {
        if (!data) return null;
        const { image, title, creatorName, albumId } = data.album;

        return (
          <Link
            to="/albums/$ALBUM_ID"
            params={{ ALBUM_ID: albumId }}
            className="no-underline group"
          >
            <Flex className="h-full items-center">
              <img
                className="w-[45px] h-[45px] rounded-[4px] object-cover"
                src={image || "/placeholders/album_art_placeholder.png"}
                alt={`${title} by ${creatorName} artwork`}
              />
              <Flex className="flex-1 pl-[16px]" direction="column">
                <Text className="font-semibold text-black mb-[2px] group-hover:underline">
                  {title}
                </Text>
                <Text className="font-medium text-text-secondary text-[12px]">
                  {creatorName}
                </Text>
              </Flex>
            </Flex>
          </Link>
        );
      },
    },
    {
      field: "type",
      headerName: "Type",
      width: 100,
      minWidth: 80,
      cellStyle: { border: "none" },
      cellRenderer: () => "Album",
    },
    {
      field: "createdAt",
      headerName: "Purchased on",
      width: 140,
      minWidth: 130,
      cellStyle: { border: "none" },
      valueFormatter: (params: ValueFormatterParams<string>) => {
        if (!params.value) return "";
        return format(parseISO(params.value), "MMM dd, yyyy");
      },
    },
    {
      field: "Export",
      headerName: "",
      width: 120,
      minWidth: 110,
      cellStyle: { border: "none" },
      cellRenderer: ({ data }: ICellRendererParams<AlbumOrderResponseDto>) => {
        if (!data) return null;
        const isDownloading = downloadingId === data.albumOrderId;

        if (data.refundedAt != null) {
          return (
            <Flex className="h-full items-center justify-end pr-[12px]">
              <Text className="text-[13px] font-medium text-red-600">
                Refunded
              </Text>
            </Flex>
          );
        }
        return (
          <Flex className="h-full items-center justify-end pr-[12px]">
            <IconButton
              data-testid="purchase-download-button"
              onClick={() => onDownloadAlbum(data)}
              variant="ghost"
              size="2"
              className="cursor-pointer"
              aria-label="Download"
              loading={isDownloading}
              // Two large downloads at once exhaust memory in an in-app
              // browser and one gets killed, so only one runs at a time.
              disabled={downloadingId !== null}
              title={
                downloadingId !== null && !isDownloading
                  ? "Another download is in progress"
                  : "Download"
              }
            >
              <DownloadIcon className="w-[20px] h-[20px] fill-current" />
            </IconButton>
          </Flex>
        );
      },
    },
  ];

  async function onDownloadAlbum(row: AlbumOrderResponseDto | undefined) {
    if (!row) return;
    setDownloadingId(row.albumOrderId);
    try {
      await downloadAlbum(row.albumOrderId);
    } catch (error) {
      // downloadAlbum has already shown the toast; re-report so the failure is
      // visible in Error Tracking. Swallowing it here is why two Userback
      // tickets were the first sign that album downloads were broken at all.
      // albumOrderId resolves to the album, buyer and tracks server-side, so
      // it is the one field that makes an Error Tracking entry actionable.
      datadogRum.addError(error, {
        source: "album-download",
        albumOrderId: row.albumOrderId,
      });
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <Flex direction="column" className="w-full mt-[20px]">
      {hasDownloadableItem && (
        <InAppBrowserDownloadNotice tone="light" className="mb-3" />
      )}
      {isMobile ? (
        <MobilePurchaseList
          items={rowData}
          downloadingId={downloadingId}
          onDownloadAlbum={onDownloadAlbum}
        />
      ) : (
        <Table data={rowData} columns={columns} domLayout="autoHeight" />
      )}
    </Flex>
  );
}

function MobilePurchaseList({
  items,
  downloadingId,
  onDownloadAlbum,
}: {
  items: AlbumOrderResponseDto[];
  downloadingId: string | null;
  onDownloadAlbum: (row: AlbumOrderResponseDto) => void;
}) {
  if (items.length === 0) {
    return (
      <Text className="text-text-secondary text-[14px] py-8 text-center">
        No purchases yet
      </Text>
    );
  }

  return (
    <div className="flex flex-col">
      {items.map((item) => (
        <MobilePurchaseRow
          key={item.albumOrderId}
          item={item}
          downloadingId={downloadingId}
          onDownloadAlbum={onDownloadAlbum}
        />
      ))}
    </div>
  );
}

function MobilePurchaseRow({
  item,
  downloadingId,
  onDownloadAlbum,
}: {
  item: AlbumOrderResponseDto;
  downloadingId: string | null;
  onDownloadAlbum: (row: AlbumOrderResponseDto) => void;
}) {
  const isRefundedAlbum = item.refundedAt != null;
  const { image, title, creatorName } = item.album;
  const isDownloading = downloadingId === item.albumOrderId;
  const dateStr = format(parseISO(item.createdAt), "MMM dd, yyyy");

  const handleDownloadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDownloadAlbum(item);
  };

  const rowContent = (
    <Flex
      data-testid="purchase-item"
      className="items-center py-3 px-1 gap-3 border-b border-[rgba(5,12,43,0.06)]"
    >
      <img
        className="w-[52px] h-[52px] rounded-[6px] object-cover flex-shrink-0"
        src={image || "/placeholders/album_art_placeholder.png"}
        alt={`${title} by ${creatorName} artwork`}
      />

      <Flex direction="column" className="flex-1 min-w-0">
        <Text className="font-semibold text-black text-[14px] truncate">
          {title}
        </Text>
        <Text className="font-medium text-text-secondary text-[12px] truncate">
          {creatorName}
        </Text>
        <Flex className="items-center gap-1.5 mt-0.5">
          <Text className="text-text-tertiary text-[11px]">Album</Text>
          <Text className="text-text-tertiary text-[11px]">&middot;</Text>
          <Text className="text-text-tertiary text-[11px]">{dateStr}</Text>
        </Flex>
      </Flex>

      <Flex className="items-center gap-2 flex-shrink-0">
        {isRefundedAlbum ? (
          <Text className="text-[13px] font-medium text-red-600">Refunded</Text>
        ) : (
          <IconButton
            data-testid="purchase-download-button"
            onClick={handleDownloadClick}
            variant="ghost"
            size="2"
            className="cursor-pointer"
            aria-label="Download"
            loading={isDownloading}
            // Same one-at-a-time rule as the table. It matters more here:
            // in-app browsers are where two concurrent downloads get one of
            // them killed, and they are reached from mobile.
            disabled={downloadingId !== null}
            title={
              downloadingId !== null && !isDownloading
                ? "Another download is in progress"
                : "Download"
            }
          >
            <DownloadIcon className="w-[20px] h-[20px] fill-current" />
          </IconButton>
        )}
      </Flex>
    </Flex>
  );

  return (
    <Link
      to="/albums/$ALBUM_ID"
      params={{ ALBUM_ID: item.album.albumId }}
      className="no-underline text-inherit"
    >
      {rowContent}
    </Link>
  );
}
